import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import * as Mutation from '../../../../api/gql/mutation'
import { CONSTRAINT_TYPES, DATED_CONSTRAINTS, MAX_DURATION_DAYS, type ConstraintType, MAX_OWNER_LENGTH } from '../../lib/constants'
import { describeScheduleError } from '../../lib/errors'
import { todayIso } from '../../lib/format'
import type { ScheduleActivity, ScheduleCalendar } from '../../types'
import IsoDatePicker from '../../components/IsoDatePicker'
import { useGanttOps } from '../gantt-ops'
import FieldRow from './FieldRow'

const CALENDAR_DEFAULT = 'default'

interface Props {
  activity: ScheduleActivity
  calendarsById: Map<string, ScheduleCalendar>
  scheduleId: string
  canEdit: boolean
}

/**
 * General fields of the activity panel (Task 17, spec B §9.7 step 2). Every field is a controlled
 * input seeded from `activity` and committed through the shared `patch()` — one
 * `updateScheduleActivities` call per commit, serialised through `GanttOps.run` like every other
 * schedule mutation, with a per-field pending flag driving `FieldRow`'s spinner.
 *
 * `ActivityPanel` mounts this with `key={activity.id}`, so the whole component remounts on focus
 * change — `pendingField`/`fieldError` (fix round 1, review finding #3) reset for free along with
 * `name`/`notes`/`duration`. The per-field seed effects below stay: within one activity's lifetime
 * (same `activity.id`, so no remount) they still need to pick up an external update to that field
 * without clobbering an unrelated in-progress edit.
 */
const GeneralSection: React.FC<Props> = ({ activity, calendarsById, scheduleId, canEdit }) => {
  const { t } = useTranslation()
  const { run } = useGanttOps()
  const [updateActivities] = useMutation(Mutation.UPDATE_SCHEDULE_ACTIVITIES)
  const [pendingField, setPendingField] = useState<string | null>(null)
  const [fieldError, setFieldError] = useState<{ field: string; message: string } | null>(null)

  const [name, setName] = useState(activity.name)
  useEffect(() => setName(activity.name), [activity.id, activity.name])
  const [notes, setNotes] = useState(activity.notes ?? '')
  useEffect(() => setNotes(activity.notes ?? ''), [activity.id, activity.notes])
  const [owner, setOwner] = useState(activity.owner ?? '')
  useEffect(() => setOwner(activity.owner ?? ''), [activity.id, activity.owner])
  const [duration, setDuration] = useState(String(activity.durationDays))
  useEffect(() => setDuration(String(activity.durationDays)), [activity.id, activity.durationDays])

  const errorFor = (field: string): string | null => (fieldError && fieldError.field === field ? fieldError.message : null)
  const clearError = (field: string) => setFieldError((e) => (e && e.field === field ? null : e))

  const patch = async (field: string, fields: Record<string, any>) => {
    setFieldError(null)
    setPendingField(field)
    try {
      await run(
        () => updateActivities({ variables: { scheduleId, patches: [{ id: activity.id, ...fields }] } }) as unknown as Promise<{ data: any }>,
        (d: any) => d.updateScheduleActivities,
      )
    } catch (e: any) {
      // `run()` already toasted the error; a per-field line is still useful once the toast is gone.
      setFieldError({ field, message: describeScheduleError(e.message, t) })
    } finally {
      setPendingField(null)
    }
  }

  const commitName = () => {
    const v = name.trim()
    if (!v || v.length > 500) {
      setFieldError({ field: 'name', message: t('Schedule_cell_invalid_name') })
      setName(activity.name)
      return
    }
    clearError('name')
    if (v === activity.name) return
    patch('name', { name: v })
  }
  const commitOwner = () => {
    const v = owner.trim() || null
    if (v && v.length > MAX_OWNER_LENGTH) {
      setFieldError({ field: 'owner', message: t('Schedule_cell_invalid_owner') })
      setOwner(activity.owner ?? '')
      return
    }
    clearError('owner')
    if (v === (activity.owner ?? null)) return
    patch('owner', { owner: v })
  }
  const commitNotes = () => {
    const v = notes.trim() ? notes : null
    if (v === (activity.notes ?? null)) return
    patch('notes', { notes: v })
  }
  const commitDuration = () => {
    const n = Number(duration)
    if (!Number.isInteger(n) || n < 1 || n > MAX_DURATION_DAYS) {
      setFieldError({ field: 'duration', message: t('Schedule_cell_invalid_int') })
      setDuration(String(activity.durationDays))
      return
    }
    clearError('duration')
    if (n === activity.durationDays) return
    patch('duration', { durationDays: n })
  }
  const onMilestoneChange = (checked: boolean) => patch('milestone', { isMilestone: checked })
  const onAnchorChange = (checked: boolean) => patch('anchor', { anchorStart: checked })
  const onCalendarChange = (value: string) => patch('calendar', { calendarId: value === CALENDAR_DEFAULT ? null : value })

  const constraintType = activity.constraintType
  const isDatedConstraint = DATED_CONSTRAINTS.indexOf(constraintType) >= 0
  const onConstraintTypeChange = (next: ConstraintType) => {
    if (DATED_CONSTRAINTS.indexOf(next) >= 0) {
      // A dated type coming from another dated type keeps its date; from ASAP it pre-fills today (spec).
      const date = isDatedConstraint && activity.constraintDate ? activity.constraintDate : todayIso()
      patch('constraint', { constraintType: next, constraintDate: date })
    } else {
      patch('constraint', { constraintType: next })
    }
  }
  const onConstraintDateChange = (value: string | null) => {
    if (!value) return // the date picker isn't clearable for a dated constraint — it always needs one
    patch('constraint', { constraintType, constraintDate: value })
  }

  const disabled = !canEdit

  return (
    <div>
      <FieldRow label={t('Schedule_field_name')} pending={pendingField === 'name'} error={errorFor('name')}>
        {(id) => <Input id={id} className="h-8 text-sm" value={name} disabled={disabled} onChange={(e) => setName(e.target.value)} onBlur={commitName} />}
      </FieldRow>
      <FieldRow label={t('Schedule_field_owner')} pending={pendingField === 'owner'} error={errorFor('owner')}>
        {(id) => <Input id={id} className="h-8 text-sm" value={owner} disabled={disabled} maxLength={MAX_OWNER_LENGTH} onChange={(e) => setOwner(e.target.value)} onBlur={commitOwner} />}
      </FieldRow>
      <FieldRow label={t('Schedule_field_notes')} pending={pendingField === 'notes'} error={errorFor('notes')}>
        {(id) => <Textarea id={id} rows={3} className="text-sm" value={notes} disabled={disabled} onChange={(e) => setNotes(e.target.value)} onBlur={commitNotes} />}
      </FieldRow>
      {!activity.isMilestone && !activity.isSummary && (
        <FieldRow label={t('Schedule_field_duration')} pending={pendingField === 'duration'} error={errorFor('duration')}>
          {(id) => (
            <Input id={id} type="number" min={1} max={MAX_DURATION_DAYS} className="h-8 text-sm" value={duration} disabled={disabled}
              onChange={(e) => setDuration(e.target.value)} onBlur={commitDuration} />
          )}
        </FieldRow>
      )}
      {!activity.isSummary && (
        <FieldRow label={t('Schedule_field_milestone')} pending={pendingField === 'milestone'}>
          {(id) => <Switch id={id} checked={activity.isMilestone} disabled={disabled} onCheckedChange={onMilestoneChange} />}
        </FieldRow>
      )}
      {activity.isMilestone && (
        <FieldRow label={t('Schedule_field_anchor_start')} pending={pendingField === 'anchor'}>
          {(id) => <Switch id={id} checked={activity.anchorStart} disabled={disabled} onCheckedChange={onAnchorChange} />}
        </FieldRow>
      )}
      <FieldRow label={t('Schedule_field_calendar')} pending={pendingField === 'calendar'}>
        {(id) => (
          <Select value={activity.calendarId ?? CALENDAR_DEFAULT} disabled={disabled} onValueChange={onCalendarChange}>
            <SelectTrigger id={id} className="h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={CALENDAR_DEFAULT}>{t('Schedule_calendar_default')}</SelectItem>
              {Array.from(calendarsById.values()).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </FieldRow>
      {!activity.isSummary && (
        <>
          <FieldRow label={t('Schedule_field_constraint')} pending={pendingField === 'constraint'}>
            {(id) => (
              <Select value={constraintType} disabled={disabled} onValueChange={(v) => onConstraintTypeChange(v as ConstraintType)}>
                <SelectTrigger id={id} className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONSTRAINT_TYPES.map((ct) => <SelectItem key={ct} value={ct}>{t(`Schedule_constraint_${ct}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </FieldRow>
          {isDatedConstraint && (
            <FieldRow label={t('Schedule_field_constraint_date')} pending={pendingField === 'constraint'}>
              {(id) => <IsoDatePicker id={id} size="sm" value={activity.constraintDate} disabled={disabled} onChange={onConstraintDateChange} />}
            </FieldRow>
          )}
        </>
      )}
    </div>
  )
}

export default GeneralSection
